/*!
 * place-time.js — время В ТОЧКЕ НА КАРТЕ, а не в поясе браузера.
 *
 * ЗАЧЕМ
 * -----
 * Тулы, которые считают астрономию для произвольной точки, показывали время
 * так: `Math.round(lon / 15) * 60` — «часовой пояс по долготе». Это правило
 * XIX века, и оно врёт дважды:
 *
 *   1. ГРАНИЦЫ ПОЯСОВ НЕ ИДУТ ПО МЕРИДИАНАМ. Испания живёт по Берлину, Китай
 *      целиком по Пекину, Индия на получасе.
 *   2. ЛЕТНЕГО ВРЕМЕНИ В ЭТОЙ ФОРМУЛЕ НЕТ ВООБЩЕ.
 *
 * Замерено 14.08.2026 на /eclipse-tracker для дефолтных городов девяти локалей:
 * время врало в ШЕСТИ городах из десяти — Париж и Мадрид на 2 часа, Лондон,
 * Берлин, Сеул и Луксор на час. Для тула, который отвечает на вопрос «во
 * сколько у меня», это не косметика: человек выходит смотреть затмение на два
 * часа позже.
 *
 * ⚠️ Пояс БРАУЗЕРА тоже не ответ: он верен ровно до первой точки, выбранной на
 * карте. Человек в Москве, смотрящий полосу над Египтом, получит египетское
 * событие по московским часам и не заметит подмены.
 *
 * ЧТО ДЕЛАЕТ
 * ----------
 *   zoneOf(lat, lon)            → 'Africa/Cairo' (offline, по вендоренной сетке)
 *   offsetMinutes(tz, date)     → 180 (с учётом ЛЕТНЕГО времени на эту дату)
 *   format(date, tz, locale, o) → «14:32:07» в поясе точки
 *   label(tz, date, locale)     → «UTC+3» / «MSK» — подпись, без которой любое
 *                                 время на экране двусмысленно
 *
 * Сетка поясов — tz-lookup (CC0, Dark Sky), вендорена в
 * public/vendor/tz-lookup/. Она даёт имя зоны IANA; всё остальное считает сам
 * браузер через Intl, поэтому переходы на летнее время всегда актуальны и нам
 * нечего обновлять.
 *
 * Fail-open: нет сетки или Intl не знает зону — возвращаем пояс браузера и
 * ЧЕСТНО помечаем это (`fallback: true`), чтобы тул мог не рисовать подпись,
 * в которой не уверен.
 */
(function (root) {
  'use strict';

  var cacheOffset = {};

  /** Имя зоны IANA по координатам. Null, если сетка не загрузилась. */
  function zoneOf(lat, lon) {
    if (typeof root.tzlookup !== 'function') return null;
    try { return root.tzlookup(lat, lon); } catch (e) { return null; }
  }

  /**
   * Смещение зоны от UTC в минутах НА ЗАДАННУЮ ДАТУ.
   *
   * Считается через Intl, а не таблицей: летнее время меняется по своим
   * правилам в каждой стране, и таблица протухает молча. Дата обязательна —
   * у одной и той же зоны смещение зимой и летом разное, и именно на этом
   * ломались «часовые пояса по долготе».
   */
  function offsetMinutes(tz, date) {
    if (!tz) return -date.getTimezoneOffset();
    var key = tz + '|' + Math.floor(date.getTime() / 3600000);
    if (cacheOffset[key] != null) return cacheOffset[key];
    var off;
    try {
      var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
      var p = {};
      dtf.formatToParts(date).forEach(function (x) { p[x.type] = x.value; });
      // hour '24' — законный ответ Intl для полуночи; Date.UTC его не понимает.
      var hh = p.hour === '24' ? 0 : Number(p.hour);
      var asUTC = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day),
        hh, Number(p.minute), Number(p.second));
      off = Math.round((asUTC - date.getTime()) / 60000);
    } catch (e) {
      off = -date.getTimezoneOffset();
    }
    cacheOffset[key] = off;
    return off;
  }

  /** Отформатировать момент в поясе точки. opts — как у toLocaleTimeString. */
  function format(date, tz, locale, opts) {
    var o = {};
    for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k];
    if (tz) {
      o.timeZone = tz;
      try { return new Intl.DateTimeFormat(locale || undefined, o).format(date); }
      catch (e) { /* зона неизвестна этому браузеру — падаем в ручной сдвиг */ }
    }
    var shifted = new Date(date.getTime() + offsetMinutes(tz, date) * 60000);
    o.timeZone = 'UTC';
    try { return new Intl.DateTimeFormat(locale || undefined, o).format(shifted); }
    catch (e2) { return shifted.toISOString().slice(11, 19); }
  }

  /**
   * Подпись пояса: «MSK», «GMT+2». Без неё время на экране двусмысленно —
   * человек не знает, местное оно, московское или UTC, и проверить не может.
   */
  function label(tz, date, locale) {
    if (tz) {
      try {
        var parts = new Intl.DateTimeFormat(locale || 'en', {
          timeZone: tz, timeZoneName: 'short', hour: '2-digit'
        }).formatToParts(date);
        for (var i = 0; i < parts.length; i++) {
          if (parts[i].type === 'timeZoneName') return parts[i].value;
        }
      } catch (e) {}
    }
    var off = offsetMinutes(tz, date);
    var sign = off < 0 ? '-' : '+';
    var a = Math.abs(off);
    var hh = Math.floor(a / 60), mm = a % 60;
    return 'UTC' + sign + hh + (mm ? ':' + (mm < 10 ? '0' : '') + mm : '');
  }

  /**
   * Всё про точку одним вызовом: зона, смещение, подпись + признак того, что
   * зону определить не удалось и мы говорим про пояс браузера.
   */
  function resolve(lat, lon, date) {
    var d = date || new Date();
    var tz = zoneOf(lat, lon);
    return {
      timeZone: tz,
      fallback: !tz,
      offsetMinutes: offsetMinutes(tz, d),
      label: label(tz, d)
    };
  }

  root.__placeTime = {
    zoneOf: zoneOf,
    offsetMinutes: offsetMinutes,
    format: format,
    label: label,
    resolve: resolve
  };
}(typeof window !== 'undefined' ? window : globalThis));
